import { useQuery } from '@apollo/client';
import { AddCircle, PeopleOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { NetworkMembershipsOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NewUserDialog } from '../components';
import { NetworkUsersDocument } from '../queries/list.generated';
import { Table } from './components';

export const UserList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const newUserParam = searchParams.get('new') === 'true';

  const { orderBy } = useOrderByParams<NetworkMembershipsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(NetworkUsersDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, ...(orderBy && { orderBy }) },
    onCompleted: (data) => handleOutOfBounds(data.network?.networkMemberships.pageInfo),
  });

  const [newUserOpen, setNewUserOpen] = useState(newUserParam);

  const networkMemberships = useMemo(() => data?.network?.networkMemberships.nodes ?? [], [data]);
  const totalCount = useTotalCount(data?.network?.networkMemberships.pageInfo.nodesCount);

  return (
    <>
      <Toolbar
        actions={
          <Button
            color="primary"
            onClick={() => setNewUserOpen(true)}
            startIcon={<AddCircle />}
            variant="contained"
          >
            {t('pages.users.buttons.new_user')}
          </Button>
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<PeopleOutlined />}
        titleText={t('pages.users.title')}
      />
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar placeholder={t('pages.users.search_placeholder')} search={search} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Count selectedCount={0} totalCount={totalCount} thing="pages.users.thing_plural" />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table loading={loading} networkMemberships={networkMemberships} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
      {newUserOpen && <NewUserDialog fullWidth onClose={() => setNewUserOpen(false)} open />}
    </>
  );
};
